import {BasePopup} from "components/popup/BasePopup"
import styles from "./BatchDailyAttendanceCheckin.module.css"
import {BaseButton, BaseLoading} from "components"
import {BaseTimePicker} from "components/DateTimePicker"
import moment, {Moment} from "moment"
import {useModel, useMutation} from "hooks"
import {useEffect, useMemo, useState} from "react"
import {Checkbox} from "antd"
import cx from "classnames"
import {get} from "lodash"
import {handleError, toastError, toastInfo, toastSuccess} from "helpers"
import {attendanceService} from "services"
import {AttendanceEventType} from "types/attendance"

const LATEST_EVENT_LABELS: Partial<Record<AttendanceEventType, string>> = {
    [AttendanceEventType.Checkin]: "Check-In",
    [AttendanceEventType.Checkout]: "Check-Out",
    [AttendanceEventType.BreakStart]: "Break Start",
    [AttendanceEventType.BreakEnd]: "Break End"
}

const getNextEventType = (latestEventType?: AttendanceEventType) => {
    if (
        [AttendanceEventType.BreakStart, AttendanceEventType.Checkout].includes(latestEventType as AttendanceEventType)
    ) {
        return AttendanceEventType.Checkin
    }
    if ([AttendanceEventType.Checkin, AttendanceEventType.BreakEnd].includes(latestEventType as AttendanceEventType)) {
        return AttendanceEventType.Checkout
    }
    return AttendanceEventType.Checkin
}

const getLatestEventLabel = (item: any, timeFormat: string) => {
    const latestEventType = item.latestEventType as AttendanceEventType | undefined
    if (!latestEventType) {
        return "—"
    }

    const timezone = get(item, ["student", "timezone"]) ?? moment.tz.guess()
    const latestEventCreatedAt =
        item.latestEventCreatedAt ||
        (() => {
            const attendances = item.attendances ?? []
            const events = attendances.flatMap((attendance) => attendance.events ?? [])
            if (!events.length) return null
            return events[events.length - 1]?.createdAt
        })()

    const eventLabel = LATEST_EVENT_LABELS[latestEventType] ?? latestEventType
    if (!latestEventCreatedAt) {
        return eventLabel
    }

    return `${eventLabel} (${moment(latestEventCreatedAt).tz(timezone).format(timeFormat)})`
}

type Props = {
    isShow: boolean
    isFetching: boolean
    students: any[]
    date: string
    onClose: () => void
    onSuccess: () => void
}

export function BatchDailyAttendanceCheckin(props: Props) {
    const {isShow, onClose, isFetching, date, students: initialStudents, onSuccess} = props
    const model = useModel()
    const dateFormat = model.getUserDateFormat()
    const timeFormat = model.getUserTimeFormat()

    const [selectedTime, setSelectedTime] = useState<Moment | null>(moment())
    const [students, setStudents] = useState(initialStudents)

    useEffect(() => {
        if (isShow && !isFetching && initialStudents.length <= 0) {
            toastInfo("There are no students to update")
            onClose()
        }
    }, [isShow, isFetching, initialStudents, onClose])

    useEffect(() => {
        if (!isShow) return
        setSelectedTime(moment())
        setStudents(
            initialStudents.map((item) => ({
                ...item,
                isChecked: false,
                nextEventType: getNextEventType(item.latestEventType)
            }))
        )
    }, [isShow, initialStudents])

    const selectedCount = useMemo(() => students.filter((item) => item.isChecked).length, [students])

    const toggleStudent = (profileId: number, checked: boolean) => {
        setStudents((prev) => prev.map((item) => (item.profileId === profileId ? {...item, isChecked: checked} : item)))
    }

    const toggleAll = (checked: boolean) => {
        setStudents((prev) => prev.map((item) => ({...item, isChecked: checked})))
    }

    const onSubmit = () => {
        if (!selectedTime) {
            toastError("Please select a time")
            return
        }

        const selectedStudents = students.filter((item) => item.isChecked)
        if (!selectedStudents.length) {
            toastError("Please select at least one student")
            return
        }

        const filterDate = moment(date)
        const baseTime = moment(selectedTime).set({
            date: filterDate.date(),
            month: filterDate.month(),
            year: filterDate.year(),
            seconds: 0,
            milliseconds: 0
        })

        const payloadStudents = selectedStudents
            .map((item) => {
                const campusId = get(item, ["student", "campuses", 0, "id"])
                const majorVersionId = get(item, ["student", "currentMajorVersionId", "id"])
                const timezone = get(item, ["student", "timezone"]) ?? moment.tz.guess()
                const profileId = item.profileId ?? item.student?.id

                return {
                    profileId,
                    campusId,
                    majorVersionId,
                    eventType: item.nextEventType,
                    timezone,
                    isMakeup: !!item.isMakeup,
                    createdAt: moment(baseTime).tz(timezone, true).toISOString()
                }
            })
            .filter(Boolean)

        if (!payloadStudents.length) {
            toastError("Selected students are missing campus information")
            return
        }

        batchCreateMutation.mutate({
            createdAt: baseTime.toISOString(),
            message: "Batch check-in/out from roll call",
            students: payloadStudents as any
        })
    }

    const batchCreateMutation = useMutation(attendanceService.batchCreateDailyAttendanceEvents, {
        onSuccess: (response) => {
            const data = response?.data ?? response
            const successCount = data?.successCount ?? 0
            const failedCount = data?.failedCount ?? 0

            if (failedCount > 0 && successCount > 0) {
                toastInfo(`Updated ${successCount} student(s). ${failedCount} failed.`)
            } else if (failedCount > 0) {
                toastError(`Failed to update ${failedCount} student(s)`)
                return
            } else {
                toastSuccess("Batch check-in/out successfully")
            }
            onSuccess()
        },
        onError: (error) => {
            handleError(error)
        }
    })

    return (
        <BasePopup isShow={isShow} onClose={onClose} width="90vw" leftIcon="PERSON_ADD" destroyOnClose keyboard={false}>
            <div className={styles.container}>
                <div className={styles.formTitle}>Batch Check-In / Check-Out on {moment(date).format(dateFormat)}</div>
                <div className={styles.timeRow}>
                    <span className={styles.timeLabel}>
                        Time<span className={styles.star}>*</span>
                    </span>
                    <BaseTimePicker
                        style={{width: 160}}
                        value={selectedTime}
                        format={timeFormat}
                        allowClear={false}
                        onChange={(value) => setSelectedTime(value)}
                        placeholder="Time"
                    />
                </div>
                <div className={styles.tableContainer}>
                    <div className={styles.rowHeader}>
                        <span className={cx(styles.headerTitle, styles.headerCenter)}>
                            <Checkbox
                                checked={students.length > 0 && selectedCount === students.length}
                                indeterminate={selectedCount > 0 && selectedCount < students.length}
                                onChange={(event) => toggleAll(event.target.checked)}
                            />
                        </span>
                        <span className={styles.headerTitle}>Student</span>
                        <span className={styles.headerTitle}>Phase</span>
                        <span className={styles.headerTitle}>Shift</span>
                        <span className={styles.headerTitle}>Major</span>
                        <span className={styles.headerTitle}>Major Version</span>
                        <span className={styles.headerTitle}>Latest Event</span>
                        <span className={styles.headerTitle}>Action</span>
                    </div>
                    <div className={styles.tableBody}>
                        {students.map((item, index) => (
                            <div
                                key={item.profileId ?? index}
                                className={cx(styles.row, {[styles.oddRow]: index % 2 === 1})}>
                                <div className={styles.checkbox}>
                                    <Checkbox
                                        checked={!!item.isChecked}
                                        onChange={(event) => toggleStudent(item.profileId, event.target.checked)}
                                    />
                                </div>
                                <div className={styles.col}>{item.studentName}</div>
                                <div className={styles.col}>{item.phaseNames}</div>
                                <div className={styles.col}>{item.shiftNames}</div>
                                <div className={styles.col}>{item.majorHtml}</div>
                                <div className={styles.col}>{item.majorVersionHtml}</div>
                                <div className={styles.col}>{getLatestEventLabel(item, timeFormat)}</div>
                                <div className={styles.col}>
                                    {[AttendanceEventType.Checkin, AttendanceEventType.BreakEnd].includes(
                                        item.nextEventType
                                    )
                                        ? "Check-In"
                                        : "Check-Out"}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className={styles.actionWrap}>
                    <BaseButton
                        title="Cancel"
                        onClick={onClose}
                        variant="secondary"
                        loading={batchCreateMutation.isLoading}
                    />
                    <BaseButton
                        title={`Submit (${selectedCount})`}
                        onClick={onSubmit}
                        disabled={selectedCount <= 0}
                        loading={batchCreateMutation.isLoading}
                    />
                </div>
            </div>
            <BaseLoading isShow={isFetching || batchCreateMutation.isLoading} />
        </BasePopup>
    )
}
